import { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

import confetti from 'canvas-confetti'
import { X, CreditCard, Ticket, Check } from 'lucide-react'
import { cn } from '@/lib/utils'

type Step = 'select' | 'confirm' | 'processing' | 'success'
type Option = 'subscription' | 'coupon'

export interface RedeemSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  balance: number
  subscriptionValue: number
  isSubscriber: boolean
  onSuccess: () => void
}

/**
 * Bottom sheet para resgate
 * - Design limpo, inspirado em iOS
 * - Animações suaves com propósito
 * - Estados claros
 */
export function RedeemSheet({
  open,
  onOpenChange,
  balance,
  subscriptionValue,
  isSubscriber,
  onSuccess,
}: RedeemSheetProps) {
  const [step, setStep] = useState<Step>('select')
  const [option, setOption] = useState<Option>(isSubscriber ? 'subscription' : 'coupon')
  const [amount, setAmount] = useState(balance)

  // Reset on close
  useEffect(() => {
    if (!open) {
      const timer = setTimeout(() => {
        setStep('select')
        setAmount(balance)
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [open, balance])

  const fireConfetti = useCallback(() => {
    const colors = ['#fff', '#30D158', '#5E5CE6']

    confetti({
      particleCount: 80,
      spread: 70,
      origin: { y: 0.6 },
      colors,
      gravity: 1.2,
    })
  }, [])

  const handleConfirm = async () => {
    setStep('processing')
    await new Promise((resolve) => setTimeout(resolve, 1500))
    setStep('success')
    fireConfetti()
    onSuccess()
  }

  const redeemAmount = option === 'subscription' ? Math.min(balance, subscriptionValue) : amount

  const formatCurrency = (val: number) =>
    val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => step !== 'processing' && onOpenChange(false)}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
          />

          {/* Sheet */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className={cn(
              'fixed bottom-0 left-0 right-0 z-50',
              'bg-[#1c1c1e] rounded-t-3xl',
              'max-h-[90vh] overflow-hidden'
            )}
          >
            {/* Handle */}
            <div className="flex justify-center py-3">
              <div className="w-9 h-1 rounded-full bg-white/20" />
            </div>

            {/* Close button */}
            {step !== 'processing' && (
              <button
                onClick={() => onOpenChange(false)}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center"
              >
                <X className="w-4 h-4 text-white/60" />
              </button>
            )}

            {/* Content */}
            <div className="px-6 pb-10">
              {/* Step: Select */}
              {step === 'select' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                  <div className="text-center pt-2">
                    <h2 className="text-xl font-semibold text-white">Usar Saldo</h2>
                    <p className="text-white/50 text-sm mt-1">
                      {formatCurrency(balance)} disponível
                    </p>
                  </div>

                  {/* Options */}
                  <div className="grid grid-cols-2 gap-3">
                    <OptionButton
                      selected={option === 'subscription'}
                      disabled={!isSubscriber}
                      onClick={() => isSubscriber && setOption('subscription')}
                      icon={<CreditCard className="w-6 h-6" />}
                      label="Assinatura"
                    />
                    <OptionButton
                      selected={option === 'coupon'}
                      onClick={() => setOption('coupon')}
                      icon={<Ticket className="w-6 h-6" />}
                      label="Cupom"
                    />
                  </div>

                  {/* Amount slider for coupon */}
                  {option === 'coupon' && (
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50">Valor</span>
                        <span className="text-white font-medium">{formatCurrency(amount)}</span>
                      </div>
                      <input
                        type="range"
                        min={1}
                        max={balance}
                        value={amount}
                        onChange={(e) => setAmount(Number(e.target.value))}
                        className="w-full accent-white"
                      />
                    </div>
                  )}

                  <button
                    onClick={() => setStep('confirm')}
                    className="w-full h-12 rounded-xl bg-white text-black font-semibold"
                  >
                    Continuar
                  </button>
                </motion.div>
              )}

              {/* Step: Confirm */}
              {step === 'confirm' && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-6"
                >
                  <div className="text-center pt-2">
                    <h2 className="text-xl font-semibold text-white">Confirmar</h2>
                  </div>

                  <div className="bg-white/5 rounded-2xl p-4 space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-white/50">Tipo</span>
                      <span className="text-white">
                        {option === 'subscription' ? 'Desconto na assinatura' : 'Cupom'}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-white/50">Valor</span>
                      <span className="text-[#30D158] font-medium">
                        {formatCurrency(redeemAmount)}
                      </span>
                    </div>
                    {option === 'subscription' && (
                      <>
                        <div className="h-px bg-white/10" />
                        <div className="flex justify-between text-sm">
                          <span className="text-white font-medium">Você pagará</span>
                          <span className="text-white font-semibold">
                            {formatCurrency(Math.max(0, subscriptionValue - balance))}
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setStep('select')}
                      className="flex-1 h-12 rounded-xl bg-white/10 text-white font-medium"
                    >
                      Voltar
                    </button>
                    <button
                      onClick={handleConfirm}
                      className="flex-1 h-12 rounded-xl bg-white text-black font-semibold"
                    >
                      Confirmar
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Step: Processing */}
              {step === 'processing' && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="py-12 flex flex-col items-center gap-4"
                >
                  <div className="w-12 h-12 border-3 border-white/20 border-t-white rounded-full animate-spin" />
                  <p className="text-white/60 text-sm">Processando...</p>
                </motion.div>
              )}

              {/* Step: Success */}
              {step === 'success' && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="py-8 flex flex-col items-center gap-4"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', damping: 10 }}
                    className="w-16 h-16 rounded-full bg-[#30D158] flex items-center justify-center"
                  >
                    <Check className="w-8 h-8 text-white" />
                  </motion.div>
                  <div className="text-center">
                    <h3 className="text-xl font-semibold text-white">Sucesso!</h3>
                    <p className="text-white/50 text-sm mt-1">
                      {formatCurrency(redeemAmount)} resgatados
                    </p>
                  </div>
                  <button
                    onClick={() => onOpenChange(false)}
                    className="mt-4 px-8 h-11 rounded-xl bg-white/10 text-white font-medium"
                  >
                    Fechar
                  </button>
                </motion.div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

// Option button component
interface OptionButtonProps {
  selected: boolean
  disabled?: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
}

function OptionButton({ selected, disabled, onClick, icon, label }: OptionButtonProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'p-4 rounded-2xl flex flex-col items-center gap-2 transition-all duration-200',
        selected ? 'bg-white text-black' : 'bg-white/5 text-white/60 hover:bg-white/10',
        disabled && 'opacity-40 cursor-not-allowed'
      )}
    >
      {icon}
      <span className="text-sm font-medium">{label}</span>
    </button>
  )
}
